
import { Users, UserCog, School } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Student } from "@/services/db";
import StudentResultCard from "./StudentResultCard";
import TeacherResultCard from "./TeacherResultCard";
import AdminResultCard from "./AdminResultCard";
import { UniversalSearchResult, SearchResultType } from "@/hooks/useUniversalSearch";

interface SearchResultsProps {
  searchResults: UniversalSearchResult;
  query: string;
  activeTab: SearchResultType;
  setActiveTab: (value: string) => void;
  highlightText: (text: string, highlight: string) => React.ReactNode;
  calculateTotalFees: (programs: { fee: number }[]) => number;
}

const SearchResults = ({
  searchResults,
  query,
  activeTab,
  setActiveTab,
  highlightText,
  calculateTotalFees
}: SearchResultsProps) => {
  const { students, teachers, admins } = searchResults;
  const totalResults = students.length + teachers.length + admins.length;

  return (
    <>
      <div className="mb-4 flex justify-between items-center">
        <p className="text-muted-foreground">
          Found {totalResults} {totalResults === 1 ? "result" : "results"} for "{query}"
        </p>
        
        <Tabs defaultValue={activeTab} onValueChange={(value) => setActiveTab(value)}>
          <TabsList>
            <TabsTrigger value="all" className="relative">
              All
              <Badge className="ml-2 h-5 px-1.5">{totalResults}</Badge>
            </TabsTrigger>
            <TabsTrigger value="students" className="relative">
              Students
              <Badge className="ml-2 h-5 px-1.5">{students.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="teachers" className="relative">
              Teachers
              <Badge className="ml-2 h-5 px-1.5">{teachers.length}</Badge>
            </TabsTrigger>
            <TabsTrigger value="admins" className="relative">
              Admins
              <Badge className="ml-2 h-5 px-1.5">{admins.length}</Badge>
            </TabsTrigger>
          </TabsList>
        
          <TabsContent value="all" className="space-y-8 mt-4">
            {/* Student Results */}
            {students.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-2 flex items-center">
                  <Users className="mr-2 h-5 w-5" />
                  Students
                </h3>
                {students.map((student) => (
                  <StudentResultCard 
                    key={`student-${student.id}`} 
                    student={student} 
                    query={query} 
                    highlightText={highlightText}
                    calculateTotalFees={calculateTotalFees}
                  />
                ))}
              </div>
            )}
            
            {/* Teacher Results */}
            {teachers.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-2 flex items-center">
                  <School className="mr-2 h-5 w-5" />
                  Teachers
                </h3>
                {teachers.map((teacher) => (
                  <TeacherResultCard 
                    key={`teacher-${teacher.id}`} 
                    teacher={teacher} 
                    query={query} 
                    highlightText={highlightText}
                  />
                ))}
              </div>
            )}
            
            {/* Admin Results */}
            {admins.length > 0 && (
              <div>
                <h3 className="text-lg font-semibold mb-2 flex items-center">
                  <UserCog className="mr-2 h-5 w-5" />
                  Administrators
                </h3>
                {admins.map((admin) => (
                  <AdminResultCard 
                    key={`admin-${admin.id}`} 
                    admin={admin} 
                    query={query} 
                    highlightText={highlightText}
                  />
                ))}
              </div>
            )}
          </TabsContent>
          
          <TabsContent value="students" className="space-y-4 mt-4">
            {students.map((student) => (
              <StudentResultCard 
                key={`student-${student.id}`} 
                student={student} 
                query={query} 
                highlightText={highlightText}
                calculateTotalFees={calculateTotalFees}
              />
            ))}
            {students.length === 0 && (
              <Card className="text-center py-6">
                <CardContent>
                  <p className="text-muted-foreground">No student results found.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          
          <TabsContent value="teachers" className="space-y-4 mt-4">
            {teachers.map((teacher) => (
              <TeacherResultCard 
                key={`teacher-${teacher.id}`} 
                teacher={teacher} 
                query={query} 
                highlightText={highlightText}
              />
            ))}
            {teachers.length === 0 && (
              <Card className="text-center py-6">
                <CardContent>
                  <p className="text-muted-foreground">No teacher results found.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
          
          <TabsContent value="admins" className="space-y-4 mt-4">
            {admins.map((admin) => (
              <AdminResultCard 
                key={`admin-${admin.id}`} 
                admin={admin} 
                query={query} 
                highlightText={highlightText}
              />
            ))}
            {admins.length === 0 && (
              <Card className="text-center py-6">
                <CardContent>
                  <p className="text-muted-foreground">No admin results found.</p>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </>
  );
};

export default SearchResults;
